import type { Programme, Settings } from './settings';

export type PackageLine = { id: string; label: string; description: string; value: number | null };

export type PackageValue = {
  programme: Programme;
  salary: number;
  hires: number;
  lines: PackageLine[];
  firstHire: number;
  additionalHire: number;
  total: number;
  feePerHire: number;
  overClaim: boolean;
};

/** Build spec 9.1: 15% of salary + checks + platform (first hire only) + programme extras. */
export function packageValue(settings: Settings, programme: Programme, salary: number, hires: number): PackageValue {
  const { calculator } = settings;
  const count = clampHires(settings, hires);
  const recruitment = Math.round(calculator.recruitmentPct * salary);
  const priced: PackageLine[] = [
    { ...calculator.coreLines.recruitment, value: recruitment },
    calculator.coreLines.checks,
    calculator.coreLines.platform,
    ...settings.programmes[programme].lines,
  ];
  const firstHire = priced.reduce((sum, line) => sum + (line.value ?? 0), 0);
  const additionalHire = firstHire - calculator.coreLines.platform.value;
  return {
    programme,
    salary,
    hires: count,
    lines: [...priced, ...calculator.includedLines.map((line) => ({ ...line, value: null }))],
    firstHire,
    additionalHire,
    total: firstHire + (count - 1) * additionalHire,
    feePerHire: settings.feePerHire,
    overClaim: firstHire > settings.overClaimThreshold,
  };
}

export function clampHires(settings: Settings, hires: number): number {
  if (!Number.isFinite(hires)) return settings.hires.min;
  return Math.min(settings.hires.max, Math.max(settings.hires.min, Math.trunc(hires)));
}

export function formatPounds(value: number): string {
  return `£${Math.round(value).toLocaleString('en-GB')}`;
}

export type AgeBand = 'age21Plus' | 'age18To20' | 'age16To17';

export type WorkingPeriodRates = {
  weeks1To4: number;
  weeks5To8: number;
  weeks9To12: number;
  week13Onwards: number;
};

/** Build spec 9.2 hourly charge rates. Not shown to SMEs; kept for the rate card and PDF. */
export function workingPeriodRates(
  settings: Settings,
  input: { ageBand: AgeBand; weeklyHours: number; salary: number; equalTreatmentHourly?: number },
): WorkingPeriodRates {
  const wp = settings.workingPeriod;
  const roleRate = input.salary / (52 * input.weeklyHours);
  const under21 = input.ageBand !== 'age21Plus';
  const rate = (payRate: number, pension: boolean, margin: number) => {
    const holiday = payRate * wp.holidayAccrual;
    const weekly = (payRate + holiday) * input.weeklyHours;
    const threshold = under21 ? wp.employerNiUnder21WeeklyCap : wp.employerNiWeeklyThreshold;
    const niable = Math.max(0, weekly - threshold);
    const ni = (niable * wp.employerNiRate) / input.weeklyHours;
    const pensionHourly = pension ? (Math.max(0, weekly - wp.pensionWeeklyLowerLimit) * wp.pensionRate) / input.weeklyHours : 0;
    const cost = (payRate + holiday + ni + pensionHourly) * (1 + wp.operatingBuffer);
    return cost * (1 + margin);
  };
  return {
    weeks1To4: rate(wp.nmwHourly[input.ageBand], false, wp.marginWeeks1To4),
    weeks5To8: rate(roleRate, false, wp.marginWeek5Onwards),
    weeks9To12: rate(roleRate, false, wp.marginWeek5Onwards),
    week13Onwards: rate(input.equalTreatmentHourly ?? roleRate, true, wp.marginWeek5Onwards),
  };
}
